import {BaseElementAsync, BASIS, OSkinableInit} from "back/commons/basis";
import {IReg, REG} from "lib/commons/registry";
import {IView} from "lib/commons/views";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {CoreUniverse, IUniverseEnv} from "lib/core/universe";
import {GridSmall, OGridSmallInit} from "back/commons/widgets/grid-tags";
import {GridColDef, IGridDataRow, IGridLineDrawer} from "back/commons/widgets/grid-core";
import {CellBuilderDate, CellBuilderEnum, CellBuilderIconLabel, CellBuilderString, GridDataHolderJsonArray, GridDataRowJson} from "back/commons/widgets/grid-libs";
import {ESkinPackErrors, ESkinPackStatus, EWspPackErrors, EWspPackStatus, IPacksInUniverse, JPackBase, JWspOptionDef, JWspPackBase, JWspPackInstalled, JWspTypeDef, PackSrv, SKINPACK, WSPPACK} from "lib/wsp/pack";
import {BarActions, OBarActionsInit} from "back/commons/widgets/bars";
import {AccelKeyMgr, Action, ACTION, IAction} from "lib/commons/actions";
import {IInstallPackActionCtx, InstallSkinPackAction, InstallWspPackAction} from "back/wsp/actions/packActions";
import {GridColTreeDef, GridDataHolderJsonTree} from "back/commons/widgets/tree";
import {POPUP} from "back/commons/widgets/popups";
import "back/wsp/dialogs/dialogs_Perms";
import {ERROR} from "lib/core/errorReport";
import {IContextMenuActionsPointer} from "back/commons/actionables";
import JSkinClassesForWspType = SKINPACK.JSkinClassesForWspType;

export interface OPacksGridInit extends OGridSmallInit {
	/** Actions associées aux items sélectionnés (menu contextuel et IFocusActionables). */
	actions?: IAction<IPackActionCtx>[]

	/** Racourcis claviers à capter sur la sel courante. */
	accelKeyMgr?: AccelKeyMgr<IPackActionCtx>

	/** Supprime le lineDrawer par défaut (avec null) ou customise le lineDrawer. */
	lineDrawer?: null | IGridLineDrawer

}

export interface OPacksMgrInit extends OSkinableInit {
	reg?: IReg<IUniverseEnv>
	universes: (CoreUniverse & IPacksInUniverse)[];
	/** Init de la liste des résultats. */
	packsGrid?: OPacksGridInit
	/** extensions autorisées pour l'installation (avec le .)
	 * La première valeur sera utilisée comme extension de téléchargement **/
	packExtensions?: string[];
}

interface PacksMgr<PACKOBJ extends JPackBase, PACKOBJGLOBAL extends IPacksGlobal & JPackBase> extends BaseElementAsync, IView, IGridLineDrawer, IPackActionCtx, IInstallPackActionCtx {
	initialize(init: OPacksMgrInit): this
}

abstract class PacksMgr<PACKOBJ extends JPackBase, PACKOBJGLOBAL extends IPacksGlobal & JPackBase> extends BaseElementAsync implements IGridLineDrawer, IPackActionCtx, IInstallPackActionCtx {

	reg: IReg<IUniverseEnv>;

	packSrvList: PackSrv[] = [];

	packsToolbar?: BarActions<IPackActionCtx> | null;

	grid: GridSmall & IContextMenuActionsPointer<IPackActionCtx>;

	fetchState: 'pending' | 'done' | 'failed';

	extension4download: string = ".pack";

	/** Packs en cours d'installation **/
	protected _installPendingPacks: Map<String, PACKOBJGLOBAL> = new Map<String, PACKOBJGLOBAL>();

	/** Dernière liste de packs retournée aprés interrogation des différents portals **/
	protected _lastPacksListFromSrvs: (PACKOBJGLOBAL)[];

	/** Liste de tous les packs installés dans cette instance packMgr (même aprés refresh) **/
	protected fullSessionInstallPacksIds: ({ packSrv: PackSrv, pack?: PACKOBJ })[] = [];

	protected async _initialize(init: OPacksMgrInit): Promise<void> {
		// Reg dédié car des actions par d&faut sont ajoutées au reg courant, et cet objet peut etre multi instancié dans un meme contexe reg
		this.reg = REG.createSubReg(this.findReg(init));

		if (init.universes)
			init.universes.forEach((univ) => {
				if (univ.packServer) this.packSrvList.push(univ.packServer);
			})

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:panel", sr);
		this._initAndInstallSkin(this.localName, init);

		if (init.packExtensions && init.packExtensions.length > 0) {
			this.extension4download = init.packExtensions[0];
			this.packExtensions = init.packExtensions;
		}

		/** Init les listes du registre **/
		this.overrideInitialize(init);
		this.reg.addToList("actions:packs:packsmgr:toolbar", "actionRefreshUi", 1, actionRefreshUi);

		/** Accelkey **/
		let accelKeyMgr = new AccelKeyMgr().initFromMapActions(this.reg.mergeListsAsMap("accelkeys:packs:packsmgr"));

		/** Toolbar **/
		let packsActions = ACTION.injectSepByGroup(this.reg.mergeLists("actions:packs:packsmgr:toolbar", "actions:packs:packsmgr") as IAction<IPackActionCtx>[], this.reg.getPref("actions.packs.packsmgr.groupOrder", "packs pack * refresh"), this);
		accelKeyMgr.initFromMapActions(this.reg.getListAsMap("accelkeys:wspApp:global"));

		this.packsToolbar = sr.appendChild(<BarActions class="horizontal" î={{
			reg: this.reg,
			actions: packsActions,
			actionContext: this,
			uiContext: "dialog",
			disableFullOverlay: true
		} as OBarActionsInit<IPackActionCtx>}/> as BarActions<IPackActionCtx>);

		/** Grid **/
		this.grid = new GridSmall();

		const initPacksGrid: OPacksGridInit = BASIS.newInit(init.packsGrid, this.reg);
		if (initPacksGrid.skinScroll === undefined) initPacksGrid.skinScroll = 'scroll/small';
		if (initPacksGrid.lineDrawer === undefined) initPacksGrid.lineDrawer = this;
		if (initPacksGrid.accelKeyMgr === undefined) initPacksGrid.accelKeyMgr = accelKeyMgr;
		initPacksGrid.dataHolder = new GridDataHolderJsonTree<IPortalPacks<PACKOBJGLOBAL> | PACKOBJGLOBAL>('packs').setOpenAll(true);
		initPacksGrid.selType = "multi";
		initPacksGrid.emptyBody = this.emptyBody.bind(this);
		initPacksGrid.skinOver = "packsmgr-grid";
		initPacksGrid.autoSelOnFocus = 'first';

		this.grid.ctxMenuActions = {
			actions: ACTION.injectSepByGroup(this.reg.mergeLists("actions:packs:packsmgr:tree", "actions:packs:packsmgr") as IAction<IPackActionCtx>[], this.reg.getPref("actions.packs.packsmgr.groupOrder", "packs pack * refresh"), this) as IAction<IPackActionCtx>[],
			actionContext: this,
		};

		this.grid.addEventListener('keydown', (ev: KeyboardEvent) => {
			accelKeyMgr.handleKeyboardEvent(ev, this);
		});

		this.grid.addEventListener('grid-select', function (this: GridSmall, ev: Event) {
			const packsMgr = DOMSH.findHost<PacksMgr<any, any>>(this);
			packsMgr.packsToolbar.refreshContent();
		});

		this.grid.initialize(initPacksGrid);
		this.grid.setAttribute("c-resizable", "");
		sr.appendChild(this.grid);

		return this.refreshUi();
	}

	/**
	 * Ajoute une couche de configuration dans le init courant
	 */
	protected overrideInitialize(init: OPacksMgrInit): void {}

	/**
	 * Refresh UI
	 * Gestion de la réentrance
	 */
	async refreshUi(scope: "local" | "full" = "full") {
		try {
			if (this._refreshPending) {
				this._newRefreshRequired = (this._newRefreshRequired == "full" ? "full" : scope);
				return;
			}
			this._refreshPending = true;
			await this._refreshUi(scope);
		} finally {
			this._refreshPending = false;
			if (this._newRefreshRequired) {
				let state = this._newRefreshRequired;
				this._newRefreshRequired = null;
				if (state == "full" && this._fullAutoRefrehPlanned)
					clearTimeout(this._fullAutoRefrehPlanned);
				setTimeout(this.refreshUi.bind(this, state), 500);
			}
		}
	}

	protected _newRefreshRequired: "local" | "full" | null;
	protected _refreshPending: boolean = false;
	protected _fullAutoRefrehPlanned: number;
	protected _showLastNotInstalledPacksOnRefreshAll: boolean = false;

	private async _refreshUi(scope: "local" | "full" = "full") {
		try {
			this.fetchState = "pending";
			let packsListByPortal: Map<PackSrv, PACKOBJGLOBAL[]> = new Map<PackSrv, PACKOBJGLOBAL[]>();

			if (this._installPendingPacks)
				this._installPendingPacks.forEach(pack => {
					if (!packsListByPortal.has(pack.packSrv))
						packsListByPortal.set(pack.packSrv, []);
					packsListByPortal.get(pack.packSrv).push(pack);
				});

			if (!this._lastPacksListFromSrvs || scope == "full")
				await this.loadPacksListFromSRvs();

			this._lastPacksListFromSrvs.forEach(pack => {
				if (!packsListByPortal.has(pack.packSrv))
					packsListByPortal.set(pack.packSrv, []);
				packsListByPortal.get(pack.packSrv).push(pack);

				if (!this.isPackStatusPending(pack.installStatus))
					pack.title = this.buildPackTitle(pack as IPacksBaseGlobal);

				// Affectation du titre si vide en exploitant les infos des derniers packs installés
				// Pertinent dans le cas des packs en cours d'installation
				if (!pack.title) {
					let installPackProps = this.fullSessionInstallPacksIds.find(value => {
						return value.pack.id === pack.id && value.packSrv === pack.packSrv;
					});
					if (installPackProps && installPackProps.pack) pack.title = installPackProps.pack.title;
				}
				// Si le pack est dans un statut "pending", on programme un refresh
				if (this.isPackStatusPending(pack.installStatus)) {
					if (this._fullAutoRefrehPlanned)
						clearTimeout(this._fullAutoRefrehPlanned);
					this._fullAutoRefrehPlanned = setTimeout(() => this.refreshUi("full"), 2000);
				}
			});

			if (this._showLastNotInstalledPacksOnRefreshAll) {
				this.fullSessionInstallPacksIds?.forEach((entry) => {
					if (entry.pack && !this.isPackStatusInstalled(entry.pack.installStatus)) {
						if (!packsListByPortal.has(entry.packSrv))
							packsListByPortal.set(entry.packSrv, []);
						packsListByPortal.get(entry.packSrv).push(Object.assign({}, entry.pack, {packSrv: entry.packSrv}) as any);
					}
				});
			}

			let finalPacksList: (IPortalPacks<PACKOBJGLOBAL> | PACKOBJGLOBAL)[] = [];
			if (this.packSrvList.length == 1) {
				// Pas de matérialisation du niveau "portal"
				packsListByPortal.forEach((packs, packSrv) => {
					finalPacksList = packs;
				});
			} else {
				packsListByPortal.forEach((packs, packSrv) => {
					finalPacksList.push({
						title: packSrv.universe.getName(),
						packSrv,
						packs,
					});
				});
			}

			this.fetchState = "done";
			(this.grid.dataHolder as GridDataHolderJsonArray<IPortalPacks<PACKOBJGLOBAL> | PACKOBJGLOBAL>).setDatas(finalPacksList);
		} catch (e) {
			this.fetchState = "failed";
			(this.grid.dataHolder as GridDataHolderJsonArray<IPortalPacks<PACKOBJGLOBAL> | PACKOBJGLOBAL>).setDatas([]);
			throw e;
		}
	}

	/**
	 *
	 */
	protected buildPackTitle(pack: IPacksBaseGlobal): string {
		return WSPPACK.buildPackTitle(pack);
	}

	/**
	 * Suivi de l'installation du pack
	 * @param file
	 * @param packSrv
	 * @param pack
	 */
	async packInstallHandler(state: "start" | "end", file: File, packSrv: PackSrv, pack?: PACKOBJ): Promise<void> {
		const installuid = file.name + "_" + file.size + "_" + packSrv.universe.getId();
		if (state == "start") {
			if (!this._installPendingPacks.get(installuid)) {
				this._installPendingPacks.set(installuid, {
					id: installuid,
					packSrv: packSrv,
					title: file.name,
					installStatus: "uploadPending"
				} as PACKOBJGLOBAL);
				await this.refreshUi("local");
			}
		} else
			this._installPendingPacks.delete(installuid);

		if (pack) {
			if (!pack.title) pack.title = file.name;
			this.fullSessionInstallPacksIds.push({packSrv, pack});
		}
	}

	async globalInstallHandler(state: "start" | "end", packsAreInstalled: boolean): Promise<void> {
		if (state == "end" && packsAreInstalled)
			await this.refreshUi("full");
	}

	/**
	 * Tree : affichage explicite des lignes en erreur
	 * @param row
	 * @param line
	 */
	redrawLine(row: IGridDataRow, line: HTMLElement): void {
		const packSrv = row.getData("packSrv") as PackSrv;
		// Statut d'installation
		const installStatus = row.getData("installStatus") as EWspPackStatus;
		const error = row.getData("error") as EWspPackErrors;
		if (installStatus == "installFailed" || error)
			DOM.addClass(line, "error");
		else if (installStatus == "uploadPending" || installStatus == "installPending")
			DOM.addClass(line, "pending");
		else {
			DOM.removeClass(line, "error");
			DOM.removeClass(line, "pending");
		}
		//
		if ((packSrv && !packSrv.universe.reg.hasPerm("action.packsMgr#uninstall.wspPack")) || row.getData("system"))
			DOM.addClass(line, "readonly");
		else
			DOM.removeClass(line, "readonly");

		// Nouvelles installations
		const packId = row.getData("id") as string;
		if (packSrv && packId && this.isNewPack(packSrv, packId)) {
			DOM.addClass(line, "new");
			setTimeout(() => {DOM.removeClass(line, "new")}, 5000);
		} else
			DOM.removeClass(line, "new");

	}

	/**
	 * Retourne true si le pack a été installé dans l'instance packMgr courante
	 * @param packSrv
	 * @param packId
	 */
	protected isNewPack(packSrv: PackSrv, packId: string): boolean {
		return (packId && packSrv && this.fullSessionInstallPacksIds.findIndex(value => {
			return value.pack.id === packId && value.packSrv === packSrv;
		}) > -1);
	}

	/**
	 * Recharge la liste des packs affichés des serveurs
	 */
	abstract loadPacksListFromSRvs(): Promise<void[]>

	/**
	 * Retourne true si le statut du pack est de type "pending"
	 */
	abstract isPackStatusPending(status: any): boolean

	/**
	 * Retourne true si le statut du pack est "installé""
	 */
	abstract isPackStatusInstalled(status: any): boolean

	/**
	 * Tree : fonction retournant le texte à afficher en cas de tree vide
	 */
	protected emptyBody() {
		switch (this.fetchState) {
		case "done":
			return <c-msg>Aucun pack installé</c-msg>;
		case "failed":
			return <c-msg level="error">Échec au chargement</c-msg>;
		default:
			return <c-msg>Chargement en cours...</c-msg>;
		}
	}

}

/** Skin du grid **/
REG.reg.registerSkin('packsmgr-grid', 1, /* language=CSS */ `
	:host {
		flex: 1;
	}

  .readonly {
	  font-style: italic;
  }

  .new {
	  background-color: var(--row-highlight-bgcolor);
  }

  .error {
	  color: var(--error-color);
  }

  .pending {
	  font-style: italic;
	  color: var(--warning-color);
  }

  .portalrow {
	  font-weight: bold;
	  color: var(--alt1-color);
	}

`);


/** Struct de donnée globale aux n packsMgr du portal */
interface IPacksGlobal {
	packSrv: PackSrv,
}


interface IPortalPacks<E extends IPacksGlobal> {
	// nom du portal
	title: string,
	packSrv: PackSrv,
	packs: E[],
}

interface IPacksBaseGlobal extends IPacksGlobal, JWspPackBase {}

interface IPacksInstalledGlobal extends IPacksGlobal, JWspPackInstalled {}


// ###
// # WspPack
// #

export class WspPacksMgr extends PacksMgr<JWspPackInstalled | JWspPackBase, IPacksBaseGlobal | IPacksInstalledGlobal> {

	protected async _initialize(init: OPacksMgrInit): Promise<void> {
		if (!init.packExtensions) init.packExtensions = [".wsppack"];
		super._initialize(init);
	}

	protected overrideInitialize(init: OPacksMgrInit): void {
		/** Actions **/
		this.reg.addToList("actions:packs:packsmgr", "actionRemovePacks", 1, RemoveWspPackAction.SINGLETON);
		this.reg.addToList("actions:packs:packsmgr", "actionDownloadPack", 1, DownloadWspPackAction.SINGLETON);

		this.reg.addToList("actions:packs:packsmgr:toolbar", "actionInstallPack", 1, InstallWspPackAction.SINGLETON);

		/** Accelkeys **/
		this.reg.addToList("accelkeys:packs:packsmgr", "Delete", 1, RemoveWspPackAction.SINGLETON);

		/** Grid **/
		let columnDefs = [];
		columnDefs.push(
			new GridColTreeDef('packTitle').setLabel("Pack")
				.setDefaultSort(1, 'ascendant')
				.setFlex('25em', 1, 1).setMinWidth('8em').setSortable(true)
				.setCellBuilder(new CellBuildeWspPackTitle()),
			new GridColDef('installDate').setLabel("Date").setDescription("Date d'installation")
				.setFlex('3em', 1, 1).setMinWidth('3em').setSortable(true)
				.setCellBuilder(new CellBuilderDate('installDate').setCellClass("center")
					.setDescriptionFunc(function (this: CellBuilderDate, row: IGridDataRow) {
						const d = this.getDate(row);
						return d ? Date.prototype.toLocaleDateString.call(this.getDate(row), "fr", {weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric"}) : "";
					})),
			new GridColDef('installStatus').setLabel("Statut")
				.setFlex('5em', 1, 1).setMinWidth('4em').setSortable(true)
				.setCellBuilder(new CellBuilderEnum('installStatus', new Map([["uploadPending", "Envoi en cours..."], ["installPending", "Installation en cours..."], ["installed", "Installé"], ["installFailed", "Non installé (échec)"]])).setCellClass("center")
					.override("_getValue", function (this: CellBuilderEnum, row: IGridDataRow): any {
						if (row.getData("packs")) {
							return "";
						} else {
							if (row.getData("system"))
								return "Système (non modifiable)"
							else {
								if (WSPPACK.isStatusInstalled(row.getData("installStatus")) && row.getData("error")) return "Non disponible";
								return this.mappingTable.get(row.getData("installStatus")) || "Disponible";
							}
						}
					})
					.setDescriptionFunc((row)=>WSPPACK.errorToString(row.getData("error") as EWspPackErrors))
				)
			,
		);


		if (!init.packsGrid) init.packsGrid = [] as OPacksGridInit;
		if (init.packsGrid.columnDefs === undefined) init.packsGrid.columnDefs = columnDefs;
	}

	isPackStatusPending(status: any): boolean {
		return WSPPACK.isStatusPending(status);
	}

	isPackStatusInstalled(status: any): boolean {
		return WSPPACK.isStatusInstalled(status);
	}

	async loadPacksListFromSRvs() {
		this._lastPacksListFromSrvs = [];
		return Promise.all(this.packSrvList.map(async (packSrv) => {
			if (packSrv.universe.reg.hasPerm("action.wspPacksMgr#list")) {
				try {
					let packs = await WSPPACK.listPacks(packSrv);
					if (packs)
						await Promise.all(packs.map((pack) => {
							let config: (IPacksBaseGlobal | IPacksInstalledGlobal) = Object.create(pack);
							config.packSrv = packSrv;
							this._lastPacksListFromSrvs.push(config);
						}));
				} catch (e) {
					POPUP.showNotifError("Rafraichissement partiel de l'écran", this.grid);
					await ERROR.log(e);
				}
			}
		}));
	}

	/**
	 * Retourne true si les packs peuvent être affichés sur au moins un packSrv
	 */
	static isAvailable(universes: (CoreUniverse & IPacksInUniverse)[]): boolean {
		if (universes) {
			return universes.findIndex((univ) => {
				return univ.reg.hasPerm("action.wspPacksMgr#list");
			}) > -1;
		}
		return false;
	}
}

customElements.define("wsp-packsmgr", WspPacksMgr);

/** Skin global du packMgr **/
REG.reg.registerSkin('wsp-packsmgr', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}
`);

/**
 * Colonne de titre du wsp pack
 */
class CellBuildeWspPackTitle extends CellBuilderIconLabel {
	constructor() {
		super('title');
	}

	protected _isPortalRow(row: IGridDataRow): boolean {
		return row.getData("packs") ? true : false;
	}

	protected _getIcon(row: IGridDataRow): string {
		if (this._isPortalRow(row))
			return null;
		const wspDef = row.getData("wspTypeDef") as (JWspTypeDef | JWspOptionDef)[];
		let wspTypeDefIndex = wspDef ? wspDef.findIndex((wspDef) => {
			return ("isOption" in wspDef) ? false : true;
		}) : null;
		return wspTypeDefIndex > -1 ? "/@skin@/wsp/dialogs/packsMgr/wspppack.svg" : "/@skin@/wsp/dialogs/packsMgr/wsppackopt.svg";
	}

	/** Packs en cours d'installation affichés en premier **/
	getColSortFn(): (r1: IGridDataRow, r2: IGridDataRow) => number {
		return (r1: IGridDataRow, r2: IGridDataRow) => {
			function isPending(status: EWspPackStatus): boolean {
				return (status == "uploadPending" || status == "installPending") ? true : false;
			}

			const v1Status = r1.getData("installStatus") as EWspPackStatus;
			const v2Status = r2.getData("installStatus") as EWspPackStatus;
			const v1 = isPending(v1Status) ? 0 : 1 + this._getValue(r1);
			const v2 = isPending(v2Status) ? 0 : 1 + this._getValue(r2);
			if (v1 == null) {
				if (v2 == null) return 0;
				return 1;
			} else if (v2 == null) {
				return -1;
			}
			return this._comparator ? this._comparator.compare(v1, v2) : v1.toString().localeCompare(v2);
		}
	}

	redrawCell(row: IGridDataRow, root: HTMLElement) {
		super.redrawCell(row, root);
		if (this._isPortalRow(row)) {
			const span = root.firstElementChild as HTMLSpanElement;
			const st = span.style;
			st.paddingInlineStart = "unset";
			st.marginInlineEnd = "unset";
			DOM.addClass(root, "portalrow");
		}
	}

	protected _getDescription(row: IGridDataRow): any {
		const installStatus = row.getData("installStatus") as EWspPackStatus;
		const buildId = row.getData("buildId");
		if (installStatus == "installFailed") {
			return "Échec à l'installation :\n"+WSPPACK.errorToString(row.getData("error") as EWspPackErrors);
		} else if (installStatus == "installed") {
			const wspTypeDef = row.getData("wspTypeDef") as (JWspTypeDef | JWspOptionDef)[];
			let modelsStr: string[] = [];
			let optionsStr: string[] = [];
			if (wspTypeDef) {
				wspTypeDef.forEach((wspDef) => {
					if (!WSPPACK.isOption(wspDef))
						modelsStr.push(" - " + WSPPACK.buildWspDefTitle(wspDef));
					else
						optionsStr.push(" - " + WSPPACK.buildWspDefTitle(wspDef));
				});
				modelsStr.sort();
				optionsStr.sort();
			}
			let fullDescStr: string[] = [];
			if(row.getData("error"))
				fullDescStr.push(WSPPACK.errorToString(row.getData("error") as EWspPackErrors));
			if (buildId)
				fullDescStr.push(`Identifiant de compilation du pack : ${buildId}`);
			if (modelsStr.length > 0) {
				fullDescStr.push(modelsStr.length == 1 ? "Modèle :" : "Modèles :");
				fullDescStr = fullDescStr.concat(modelsStr);
			}
			if (optionsStr.length > 0) {
				fullDescStr.push(optionsStr.length == 1 ? "Extension :" : "Extensions :");
				fullDescStr = fullDescStr.concat(optionsStr);
			}
			return fullDescStr.join("\n");
		} else
			return "";
	}
}

/**
 * Action : suppression de packs
 */
export class RemoveWspPackAction extends Action<IPackActionCtx> {
	protected uninstallOnSrvPerm = "action.packsMgr#uninstall.wspPack";

	constructor(id?: string) {
		super(id || "removeWspPack");
		this._label = "Désinstaller";
		this._description = "Désinstaller un modèle documentaire";
		this._group = "packs";
		this.setIcon("/@skin@/wsp/dialogs/packsMgr/deletePack.svg");
	}

	protected async doUninstallOnSrv(packServer: PackSrv, packId: string): Promise<void> {
		return WSPPACK.uninstallPack(packServer, packId);
	}

	isEnabled(ctx: IPackActionCtx): boolean {
		const packsMgr = DOMSH.findHost<PacksMgr<any, any>>(ctx.grid);
		let selectedPacks = (ctx.grid.dataHolder as GridDataHolderJsonArray<IPortalPacks<IPacksBaseGlobal | IPacksInstalledGlobal> | IPacksBaseGlobal | IPacksInstalledGlobal>).getSelectedDatas();
		if (!selectedPacks || selectedPacks.length == 0) return false;
		let hasUnauthorizedPack = false;
		let hasAuthorizedPack = false;
		selectedPacks.forEach((pack) => {
			if (pack.packSrv && "id" in pack) {
				pack = pack as IPacksBaseGlobal;
				if ((pack as IPacksInstalledGlobal).system || !packsMgr.isPackStatusInstalled(pack.installStatus) || !pack.packSrv.universe.reg.hasPerm(this.uninstallOnSrvPerm)) {
					hasUnauthorizedPack = true;
					return;
				} else
					hasAuthorizedPack = true;
			}
		});
		return (!hasUnauthorizedPack && hasAuthorizedPack) ? super.isEnabled(ctx) : false;
	}

	isVisible(ctx: IPackActionCtx): boolean {
		const packsMgr = DOMSH.findHost<PacksMgr<any, any>>(ctx.grid);
		if (packsMgr.packSrvList && packsMgr.packSrvList.findIndex((packSrv) => {
			return packSrv.universe.reg.hasPerm(this.uninstallOnSrvPerm);
		}) == -1) return false;
		return super.isVisible(ctx as any);
	}

	async execute(ctx: IPackActionCtx, ev?: Event): Promise<void> {
		const packsMgr = DOMSH.findHost<PacksMgr<any, any>>(ctx.grid);
		try {
			let selectedDatas = (ctx.grid.dataHolder as GridDataHolderJsonArray<IPortalPacks<IPacksBaseGlobal | IPacksInstalledGlobal> | IPacksBaseGlobal | IPacksInstalledGlobal>).getSelectedDatas();
			let filteredSelectedPacks: (IPortalPacks<IPacksBaseGlobal | IPacksInstalledGlobal> | IPacksBaseGlobal | IPacksInstalledGlobal)[] = [];
			selectedDatas.forEach((pack) => {
				if (pack.packSrv && "id" in pack)
					filteredSelectedPacks.push(pack);
			});
			// FIXME : proposer d'étendre la désinstallation aux packs similaires des autres repos
			const firstPackName = filteredSelectedPacks.length > 0 ? filteredSelectedPacks[0].title : "";
			const nbPacks = filteredSelectedPacks.length;
			if (await POPUP.confirm(filteredSelectedPacks.length == 1 ? `Voulez-vous vraiment désinstaller le pack '${firstPackName}' ?` : `Voulez-vous vraiment désinstaller les ${nbPacks} packs sélectionnés ?`, ctx.grid, {
				okLbl: "Désinstaller",
				cancelLbl: "Annuler"
			})) {
				await Promise.all(Array.from(filteredSelectedPacks).map(async (pack) => {
					return this.doUninstallOnSrv(pack.packSrv, (pack as IPacksInstalledGlobal).id);
				}));
				await packsMgr.refreshUi("full");
			}
		} catch (e) {
			POPUP.showNotifError("La désinstallation n'a pas pu avoir lieu dans son intégralité", ctx.grid);
			await ERROR.log(e);
			await packsMgr.refreshUi("full");
		}
	}

	static SINGLETON = new RemoveWspPackAction();
}


/**
 * Action : téléchargement d'un wsp pack
 */
export class DownloadWspPackAction extends Action<IPackActionCtx> {
	protected downloadOnSrvPerm = "action.packsMgr#download.wspPack";

	constructor(id?: string) {
		super(id || "downloadWspPack");
		this._label = "Télécharger...";
		this._description = "Télécharger un modèle documentaire";
		this._group = "packs";
		this.setIcon("/@skin@/wsp/dialogs/packsMgr/downloadPack.svg");
	}

	protected downloadPackUrlOnSrv(packServer: PackSrv, packId: string): string {
		return WSPPACK.getDownloadPackUrl(packServer, packId);
	}

	isEnabled(ctx: IPackActionCtx): boolean {
		const packsMgr = DOMSH.findHost<PacksMgr<any, any>>(ctx.grid);
		let selectedPacks = (ctx.grid.dataHolder as GridDataHolderJsonArray<IPortalPacks<IPacksBaseGlobal | IPacksInstalledGlobal> | IPacksBaseGlobal | IPacksInstalledGlobal>).getSelectedDatas();
		if (!selectedPacks || selectedPacks.length != 1) return false;
		let pack = selectedPacks[0] as IPacksInstalledGlobal;
		if (!pack.packSrv || !("id" in selectedPacks[0]) || (pack as IPacksInstalledGlobal).system || !packsMgr.isPackStatusInstalled(pack.installStatus)) return false;
		if (!pack.packSrv.universe.reg.hasPerm(this.downloadOnSrvPerm)) return false;
		return super.isEnabled(ctx);
	}

	isVisible(ctx: IPackActionCtx): boolean {
		const packsMgr = DOMSH.findHost<PacksMgr<any, any>>(ctx.grid);
		if (packsMgr.packSrvList && packsMgr.packSrvList.findIndex((packSrv) => {
			return packSrv.universe.reg.hasPerm(this.downloadOnSrvPerm);
		}) == -1) return false;
		return super.isVisible(ctx as any);
	}

	async execute(ctx: IPackActionCtx, ev?: Event): Promise<void> {
		const packsMgr = DOMSH.findHost<PacksMgr<any, any>>(ctx.grid);
		try {
			//const packsMgr = DOMSH.findHost<PacksMgr<any, any>>(ctx.grid);
			let selectedPacks = (ctx.grid.dataHolder as GridDataHolderJsonArray<IPortalPacks<IPacksBaseGlobal | IPacksInstalledGlobal> | IPacksBaseGlobal | IPacksInstalledGlobal>).getSelectedDatas();
			let pack = selectedPacks[0] as IPacksInstalledGlobal;
			/*const blobRef =  URL.createObjectURL(await WSPPACK.downloadPack(pack.packSrv, pack.id));
			// FIXME : Content-Disposition: attachment;filename=pack.wsppack fourni par la response non pris en compte dans ce contexte, meme en l'absence de @download
			const a = <a href={blobRef} download={IO.getValidFileName(pack.title || pack.id, packsMgr.extension4download, "dateTime")}/> as HTMLAnchorElement;
			URL.revokeObjectURL(blobRef);
			*/
			const a = <a href={this.downloadPackUrlOnSrv(pack.packSrv, pack.id)}/> as HTMLAnchorElement;
			a.click();
		} catch (e) {
			await ERROR.report("Téléchargement impossible", e);
		}
	}

	static SINGLETON = new DownloadWspPackAction();
}

// ###
// # SkinPack
// #
export class SkinPacksMgr extends PacksMgr<JWspPackInstalled | JWspPackBase, IPacksBaseGlobal | IPacksInstalledGlobal> {

	protected async _initialize(init: OPacksMgrInit): Promise<void> {
		if (!init.packExtensions) init.packExtensions = [".skinpack"];
		super._initialize(init);
	}

	protected overrideInitialize(init: OPacksMgrInit): void {
		/** Actions **/
		this.reg.addToList("actions:packs:packsmgr", "actionRemovePacks", 1, RemoveSkinPackAction.SINGLETON);
		this.reg.addToList("actions:packs:packsmgr", "actionDownloadPack", 1, DownloadSkinPackAction.SINGLETON);

		this.reg.addToList("actions:packs:packsmgr:toolbar", "actionInstallPack", 1, InstallSkinPackAction.SINGLETON);

		/** Accelkeys **/
		this.reg.addToList("accelkeys:packs:packsmgr", "Delete", 1, RemoveSkinPackAction.SINGLETON);

		/** Grid **/
		let columnDefs = [];
		columnDefs.push(
			new GridColTreeDef('packTitle').setLabel("Habillage graphique")
				.setDefaultSort(1, 'ascendant')
				.setFlex('25em', 1, 1).setMinWidth('8em').setSortable(true)
				.setCellBuilder(new CellBuildedSkinPackTitle()),
			new GridColDef('usedInModels').setLabel("Modèles")
				.setDescription("Modèles installés utilisant cet habillage graphique")
				.setFlex('3em', 1, 1).setMinWidth('3em').setSortable(false)
				.setCellBuilder(new CellBuilderSkinPackModels(this)),
			new GridColDef('installDate').setLabel("Date").setDescription("Date d'installation")
				.setFlex('3em', 1, 1).setMinWidth('3em').setSortable(true)
				.setCellBuilder(new CellBuilderDate('installDate').setCellClass("center")
					.setDescriptionFunc(function (this: CellBuilderDate, row: IGridDataRow) {
						const d = this.getDate(row);
						return d ? Date.prototype.toLocaleDateString.call(this.getDate(row), "fr", {weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric"}) : "";
					})),
			new GridColDef('installStatus').setLabel("Statut")
				.setFlex('5em', 1, 1).setMinWidth('4em').setSortable(true)
				.setCellBuilder(new CellBuilderEnum('installStatus', new Map([["uploadPending", "Envoi en cours..."], ["installed", "Installé"], ["installFailed", "Non installé (échec)"]])).setCellClass("center")
					.override("_getValue", function (this: CellBuilderEnum, row: IGridDataRow): any {
						if (row.getData("packs")) {
							return "";
						} else {
							if (row.getData("system"))
								return "Système (non modifiable)"
							else {
								if (SKINPACK.isStatusInstalled(row.getData("installStatus")) && row.getData("error")) return "Non disponible";
								return this.mappingTable.get(row.getData(this.dataKey)) || "Installé";
							}
						}
					})
				),
		);

		if (!init.packsGrid) init.packsGrid = [] as OPacksGridInit;
		if (init.packsGrid.columnDefs === undefined) init.packsGrid.columnDefs = columnDefs;
	}

	isPackStatusPending(status: any): boolean {
		return SKINPACK.isStatusPending(status);
	}

	isPackStatusInstalled(status: any): boolean {
		return SKINPACK.isStatusInstalled(status);
	}

	// Le svc des skins ne préserve pas l'infos des derniers skins installés en erreur => préservation UI
	_showLastNotInstalledPacksOnRefreshAll: boolean = true;

	protected buildPackTitle(pack: IPacksBaseGlobal): string {
		return SKINPACK.buildPackTitle(pack);
	}


	async loadPacksListFromSRvs() {
		this._lastPacksListFromSrvs = [];

		return Promise.all(this.packSrvList.map(async (packSrv) => {
			if (packSrv.universe.reg.hasPerm("action.skinPacksMgr#list")) {
				try {
					// - Liste des packs
					let packs = await SKINPACK.list(packSrv);
					if (packs)
						await Promise.all(packs.map((pack) => {
							let config: (IPacksBaseGlobal | IPacksInstalledGlobal) = Object.create(pack);
							config.packSrv = packSrv;
							this._lastPacksListFromSrvs.push(config);
						}));
				} catch (e) {
					POPUP.showNotifError("Rafraichissement partiel de l'écran", this.grid);
					await ERROR.log(e);
				}
			}
		}));
	}

	/**
	 * Retourne true si les packs peuvent être affichés sur au moins un packSrv
	 */
	static isAvailable(universes: (CoreUniverse & IPacksInUniverse)[]): boolean {
		if (universes) {
			return universes.findIndex((univ) => {
				return univ.reg.hasPerm("action.wspPacksMgr#list");
			}) > -1;
		}
		return false;
	}

	protected emptyBody() {
		switch (this.fetchState) {
		case "done":
			return <c-msg>Aucun habillage graphique installé</c-msg>;
		case "failed":
			return <c-msg level="error">Échec au chargement</c-msg>;
		default:
			return <c-msg>Chargement en cours...</c-msg>;
		}
	}
}

customElements.define("skin-packsmgr", SkinPacksMgr);

/** Skin global du packMgr **/
REG.reg.registerSkin('skin-packsmgr', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}
`);

/**
 * Colonne de titre du wsp pack
 */
class CellBuildedSkinPackTitle extends CellBuildeWspPackTitle {

	protected _getIcon(row: IGridDataRow): string {
		const packSrv = row.getData("packSrv") as PackSrv;
		const skinSetCode = row.getData("skinSetCode");
		const hasSkinSetIcon = row.getData("skinSetIcon") ? true : false;
		const installStatus = row.getData("installStatus") as ESkinPackStatus;
		if (this._isPortalRow(row))
			return null;
		return ((!SKINPACK.isStatusPending(installStatus) && packSrv && skinSetCode && hasSkinSetIcon) ? SKINPACK.getIconUrl(packSrv, skinSetCode) : "") || "/@skin@/wsp/dialogs/packsMgr/skinppack.svg";
	}

	protected _getDescription(row: IGridDataRow): any {
		let fullDescStr: string[] = [];
		if (row.getData("error"))
			fullDescStr.push(SKINPACK.errorToString(row.getData("error") as ESkinPackErrors));
		// - buildId
		const buildId = row.getData("buildId");
		if (buildId)
			fullDescStr.push(`Identifiant de compilation du pack : ${buildId}`);
		// - skinClasses
		const skinClasses: string[] = row.getData("skinClasses");
		if (skinClasses && skinClasses.length) {
			fullDescStr.push(`Classes de skin stylées par cet habillage graphique :`);
			skinClasses.forEach((entry) => fullDescStr.push(` - ${entry}`))
		}
		return fullDescStr.join("\n");
	}
}

/** Cellule donnant les modèles installés compatibles avec le skinPack */
export class CellBuilderSkinPackModels extends CellBuilderString {
	constructor(protected packsMgr: PacksMgr<any, any>) {
		super(null);
	}

	redrawCell(row: GridDataRowJson<IPacksBaseGlobal | IPacksInstalledGlobal>, root: HTMLElement): void {
		const cacheHolder = row.cacheHolder;
		let cache: JSkinClassesForWspType[] | Promise<JSkinClassesForWspType[]> = cacheHolder["-models"];
		const rowDatas = row.rowDatas;

		if (cache == null) {
			DOM.setTextContent(root, '...');
			cacheHolder["-models"] = cache = SKINPACK.ListSkinClassesForWspType(rowDatas.packSrv, rowDatas.id).then((resp) => {
				if (cacheHolder["-models"] !== cache) return; //raceCond : les données de cette row ont évoluées.
				return cacheHolder["-models"] = resp;
			});
		}

		if (cache instanceof Promise) {
			//cache non reseté mais calcul en cours, on attend
			DOM.setTextContent(root, '...');
			(root as any)._modelsPending = cache; //raceCond pour refresh async de la line HTML.
			cache.then(() => {
				if ((root as any)._modelsPending !== cache) return; //raceCond : cette line HTML a été redessinée
				(root as any)._modelsPending = null; //cleanup
				redrawCellUi.call(this);
			});
		} else {
			(root as any)._modelsPending = null; //raceCond pour refresh async de la line HTML.
			redrawCellUi.call(this);
		}

		function redrawCellUi(this: CellBuilderSkinPackModels) {
			DOM.setTextContent(root, null);
			const skinClassesForWspType: JSkinClassesForWspType[] = cacheHolder["-models"];
			if (skinClassesForWspType && skinClassesForWspType.length) {
				const list = skinClassesForWspType.sort((e1, e2) => {
					if ((e1.wspTypeRef as JWspOptionDef).isOption && !(e2.wspTypeRef as JWspOptionDef).isOption) return 1;
					if ((e2.wspTypeRef as JWspOptionDef).isOption && !(e1.wspTypeRef as JWspOptionDef).isOption) return -11;
					return e2.wspTypeRef.title.localeCompare(e1.wspTypeRef.title);
				});
				if (skinClassesForWspType.length < 3) {
					DOM.setTextContent(root, skinClassesForWspType.map((entry) => entry.wspTypeRef.title).join(", "));
				} else {
					const base = skinClassesForWspType.slice(0, 2).map((entry) => entry.wspTypeRef.title).join(", ");
					DOM.setTextContent(root, `${base}...`);
				}
				root.title = "Modèles installés utilisant cet habillage graphique :\n";
				root.title += skinClassesForWspType.map((entry) => `- ${entry.wspTypeRef.title}`).join("\n");
			} else {
				DOM.setTextContent(root, 'Aucun');
				delete root.title;
			}
			//DOM.setAttr(refElt, "href", cacheHolder["-models"]);
			//DOM.setTextContent(refElt, cacheHolder["-models"]);
		}

	}
}

/**
 * Action : téléchargement d'un skin pack
 */
export class DownloadSkinPackAction extends DownloadWspPackAction {
	protected downloadOnSrvPerm = "action.packsMgr#download.skinPack";

	constructor(id?: string) {
		super(id || "downloadSkinPack");
		this._label = "Télécharger...";
		this._description = "Télécharger l'habillage graphique";
		this._group = "packs";
		this.setIcon("/@skin@/wsp/dialogs/packsMgr/downloadPack.svg");
	}

	protected downloadPackUrlOnSrv(packServer: PackSrv, packId: string): string {
		return SKINPACK.getDownloadPackUrl(packServer, packId);
	}

	static SINGLETON = new DownloadSkinPackAction();
}

/**
 * Action : suppression de packs
 */
export class RemoveSkinPackAction extends RemoveWspPackAction {
	protected uninstallOnSrvPerm = "action.packsMgr#uninstall.skinPack";

	constructor(id?: string) {
		super(id || "removeSkinPack");
		this._label = "Désinstaller";
		this._description = "Désinstaller l'habillage graphique";
		this._group = "packs";
		this.setIcon("/@skin@/wsp/dialogs/packsMgr/deletePack.svg");
	}

	protected async doUninstallOnSrv(packServer: PackSrv, packId: string): Promise<void> {
		return SKINPACK.deleteSkin(packServer, packId);
	}

	static SINGLETON = new RemoveSkinPackAction();
}


/**
 * Contexte d'un ou plusieurs packs
 */
export interface IPackActionCtx {
	reg: IReg<any>
	grid: GridSmall
}


/** Actions **/

/**
 * Refresh UI
 */
const actionRefreshUi = new Action<any>("refreshUi")
	.setLabel("Rafraichir")
	.setGroup("refresh")
	.setIcon("/@skin@/commons/icons/refresh.svg")
	.setExecute(async function (this: Action<any>, ctx: PacksMgr<any, any>, ev: Event) {
			await ctx.refreshUi();
		}
	);

